import { Fund, ValueOf } from "creso-core/types";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DataTable,
  cn,
} from "web-design-system";
import { ChevronDown } from "lucide-react";
import get from "lodash/get";
import { FundInfoCell } from "./fund-info-cell";
import { fundInfoRowConfig } from "./config";

const columnHelper = createColumnHelper<{
  label: string;
  [key: string]: string;
}>();

export function FundInfo({
  funds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const columns = useMemo(() => {
    const firstColumn = [
      columnHelper.accessor("label", {
        header: "",
        meta: {
          className: "bg-muted px-5 py-3",
        },
        size: labelWidth,
        maxSize: labelWidth,
      }),
    ];
    return firstColumn
      .concat(
        funds.map((fund, index) => {
          return columnHelper.accessor(fund.fund_id, {
            header: "",
            meta: {
              className: `bg-background text-left px-5 py-3 text-sm ${
                index === funds.length - 1 ? "" : "border-r border-border"
              }`,
            },
            cell: ({ row }) => {
              return <FundInfoCell label={row.original.label} fund={fund} />;
            },
            size: Number.MAX_SAFE_INTEGER,
          });
        })
      )
      .concat(
        variant === "default"
          ? [
              columnHelper.accessor("space", {
                header: "",
                size: 80,
                meta: {
                  className: "bg-background",
                },
              }),
            ]
          : []
      );
  }, [funds, labelWidth, variant]);

  const data = useMemo(() => {
    return fundInfoRowConfig.map((config) => {
      const values = funds.reduce(
        (acc, fund) => {
          acc[fund.fund_id] = get(fund, config.accessor);
          return acc;
        },
        {} as Record<Fund["fund_id"], ValueOf<Fund>>
      );
      return {
        label: config.label,
        ...values,
      } as { label: string; [key: Fund["fund_id"]]: string };
    });
  }, [funds]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className={cn(
          "w-full bg-muted rounded-lg overflow-hidden border border-border group"
        )}
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full justify-between items-center px-5 py-4",
            variant === "report" && "pointer-events-none"
          )}
        >
          <span className="font-semibold text-base">Fund Info</span>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <DataTable
            className="border-none [&_table]:table-fixed"
            noHeader
            pagination={false}
            table={table}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
